import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Vault as VaultIcon, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import AuthPage from "./AuthPage";

const INVITE_BG = "https://images.unsplash.com/photo-1536138746221-2c489206ff6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGx1eHVyeSUyMHRleHR1cmV8ZW58MHx8fGJsYWNrfDE3ODY4Mjk4MDh8MA&ixlib=rb-4.1.0&q=85";

export default function FamilyLanding({ inviteCode }) {
  const [family, setFamily] = useState(null);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/families/landing/${inviteCode}`);
        setFamily(data);
      } catch {
        setInvalid(true);
      }
    })();
  }, [inviteCode]);

  if (invalid) {
    return (
      <div className="min-h-screen w-full grain grid place-items-center px-7 text-center" style={{ background: "var(--bg-base)" }} data-testid="landing-invalid">
        <div>
          <h1 className="font-serif-display text-3xl">Invite not found</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
            This invite link is invalid or has expired. Ask your family admin for a new one.
          </p>
        </div>
      </div>
    );
  }

  if (!family) return <div className="min-h-screen w-full grid place-items-center" style={{ background: "var(--bg-base)" }}><Loader2 className="animate-spin gold-text" /></div>;

  return (
    <div className="min-h-screen w-full grain relative" style={{ background: "var(--bg-base)" }} data-testid="family-landing">
      <div className="lg:grid lg:grid-cols-2 min-h-screen">
        {/* Left brand / family panel — desktop only */}
        <div className="hidden lg:block relative overflow-hidden" data-testid="landing-brand-panel">
          <img src={INVITE_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(11,11,18,0.9), rgba(11,11,18,0.62))" }} />
          <div className="relative z-10 h-full flex flex-col justify-between p-14">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl grid place-items-center gold-border" style={{ background: "rgba(20,20,31,0.6)" }}>
                <VaultIcon size={22} className="gold-text" />
              </div>
              <span className="font-serif-display text-2xl">The Family Vault</span>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--text-secondary)" }}>You're invited to join</div>
              <h2 className="font-serif-display text-6xl leading-[1.02] gold-text">{family.family_name}</h2>
              <p className="mt-5 text-base max-w-sm" style={{ color: "var(--text-secondary)" }}>
                Create your account to step inside your family's private vault — lineage, gatherings, memories and more.
              </p>
            </div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>© {new Date().getFullYear()} The Family Vault</div>
          </div>
        </div>

        {/* Register column */}
        <div className="relative z-10 flex items-center justify-center px-6 py-14 min-h-screen">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
            <div className="h-14 w-14 rounded-2xl grid place-items-center gold-border mb-6"
              style={{ background: "var(--bg-elevated)", boxShadow: "0 0 24px var(--shell-glow)" }}>
              <VaultIcon size={26} className="gold-text" />
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl leading-none gold-text" data-testid="landing-family-name">{family.family_name}</h1>
            <h2 className="font-serif-display text-2xl leading-none mt-1" style={{ color: "var(--text-secondary)" }}>The Family Vault</h2>
            <p className="mt-4 text-sm" style={{ color: "var(--text-secondary)" }} data-testid="landing-cta-text">
              Create an account to join our family vault.
            </p>
            <div className="mt-8">
              <AuthPage defaultMode="register" hideBranding invitedFamily={family.family_name} />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
